/**
 * Observers domain — store integration layer.
 *
 * Mirrors remindersStoreIntegration.ts: isolates the observers concern with
 * zero coupling to other store integrations. CRUD mutations are optimistic —
 * the toggle and delete update the store immediately and revert/restore on
 * API failure so the UI stays responsive.
 */

import { logger } from '@/utils/logger';

import { useAppStore } from './useAppStore';

import type { ObserverCreate, ObserverUpdate } from '@features/observers/types';

/**
 * Enhanced observers service that integrates with store
 */
export const observersService = {
  /**
   * Load the full observer list into the store, managing loading/error state.
   */
  refreshObservers: async (): Promise<void> => {
    const store = useAppStore.getState();

    try {
      store.setIsLoadingObservers(true);
      store.setObserversError(null);

      // Dynamic import to avoid circular dependencies
      const { observersApi } = await import('@features/observers/services/observersApi');
      store.setObservers(await observersApi.listObservers());
    } catch (error) {
      logger.error('Error loading observers:', error);
      store.setObserversError(error instanceof Error ? error.message : 'Failed to load observers');
    } finally {
      store.setIsLoadingObservers(false);
    }
  },

  /**
   * Create an observer and upsert it into the store.
   */
  createObserver: async (data: ObserverCreate) => {
    const store = useAppStore.getState();

    try {
      const { observersApi } = await import('@features/observers/services/observersApi');
      const observer = await observersApi.createObserver(data);
      store.upsertObserver(observer);
      return observer;
    } catch (error) {
      logger.error('Error creating observer:', error);
      throw error;
    }
  },

  /**
   * Update an observer. Optimistically applies an enabled-toggle and reverts
   * the prior value if the API call fails.
   */
  updateObserver: async (id: number, data: ObserverUpdate) => {
    const store = useAppStore.getState();
    const prev = store.observers.find(o => o.id === id);

    if (prev && typeof data.enabled === 'boolean') {
      store.upsertObserver({ ...prev, enabled: data.enabled });
    }

    try {
      const { observersApi } = await import('@features/observers/services/observersApi');
      const observer = await observersApi.updateObserver(id, data);
      store.upsertObserver(observer);
      return observer;
    } catch (error) {
      logger.error('Error updating observer:', error);
      if (prev) store.upsertObserver(prev);
      throw error;
    }
  },

  /**
   * Delete an observer. Optimistically removes it and restores the full list
   * if the API call fails.
   */
  deleteObserver: async (id: number) => {
    const store = useAppStore.getState();
    const prev = store.observers;
    store.removeObserver(id);

    try {
      const { observersApi } = await import('@features/observers/services/observersApi');
      await observersApi.deleteObserver(id);
    } catch (error) {
      logger.error('Error deleting observer:', error);
      store.setObservers(prev);
      throw error;
    }
  },
};
